import React, {
  ReactNode,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { X } from "lucide-react";
import { SearchBox } from "../../../../../components/Search";
import "../ModTable/ModIndex.css";
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  width?: string;
  showCloseButton?: boolean;
  closeOnClickOutside?: boolean;
  closeOnEsc?: boolean;
}

const comparableFields = [
  "vc",
  "total_capacity",
  "available",
  "must_run",
  "margin_buy",
  "margin_conservative_sell",
  "margin_aggressive_sell",
] as const;

const numericFieldSet = new Set(comparableFields);

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  width = "600px",
  showCloseButton = true,
  closeOnClickOutside = true,
  closeOnEsc = true,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      requestAnimationFrame(() => setIsAnimating(true));
    } else {
      setIsAnimating(false);
      const t = setTimeout(() => setIsMounted(false), 160);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  useEffect(() => {
    document.body.style.overflow = isMounted ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMounted]);

  useEffect(() => {
    if (!closeOnEsc || !isMounted) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [closeOnEsc, isMounted, onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnClickOutside && e.target === e.currentTarget) onClose();
  };

  if (!isMounted) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 9999 }}
      onClick={handleOverlayClick}
    >
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-200 ease-in-out ${
          isAnimating ? "opacity-50" : "opacity-0"
        }`}
      />
      <div
        ref={modalRef}
        className={`relative bg-white shadow-xl flex flex-col overflow-hidden transition-all duration-200 ease-in-out ${
          isAnimating
            ? "opacity-100 translate-y-0 scale-100"
            : "opacity-0 translate-y-4 scale-95"
        }`}
        style={{
          width,
          maxWidth: "100vw",
          maxHeight: "90vh",
          borderRadius: "12px",
          border: "3px solid #F3F5F8",
          zIndex: 10000,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
};

const ModFilter = ({
  isOpen,
  onClose,
  uniqueBlocks = [],
  uniqueGeneratorTypes = [],
  uniqueOwnership = [],
  data = [],
  handleSubmit,
  loading,
  onLocalApply,
}: any) => {
  const [selectedGeneratorType, setSelectedGeneratorType] = useState<any[]>([]);
  const [selectedOwnership, setSelectedOwnership] = useState<any[]>([]);
  const [selectedGeneratorName, setSelectedGeneratorName] = useState<any[]>([]);
  const [modifiedData, setModifiedData] = useState<any[]>([]);

  const [declaredCapacity, setDeclaredCapacity] = useState("");
  const [variableCharge, setVariableCharge] = useState("");
  const [mustRun, setMustRun] = useState("");
  const [buyMargin, setBuyMargin] = useState("");
  const [sellMargin, setSellMargin] = useState("");
  const [sellMarginAggressive, setSellMarginAggressive] = useState("");
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [selectedGenerators, setSelectedGenerators] = useState<string[]>([]);

  // Sync modifiedData with incoming data (shallow replace)
  useEffect(() => {
    setModifiedData(data);
  }, [data]);

  const resetForm = useCallback(() => {
    setDeclaredCapacity("");
    setVariableCharge("");
    setMustRun("");
    setBuyMargin("");
    setSellMargin("");
    setSellMarginAggressive("");
    setSelectedGeneratorName([]);
    setSelectedBlock([]);
    setSelectedGenerators([]);
    setSelectedOwnership([]);
    setSelectedGeneratorType([]);
  }, []);

  const removeGenerator = (name: string) =>
    setSelectedGenerators((p) => p.filter((g) => g !== name));

  const selectedTypeValues = useMemo(
    () => selectedGeneratorType.map((t: any) => t.value ?? t),
    [selectedGeneratorType]
  );
  const selectedOwnershipValues = useMemo(
    () => selectedOwnership.map((o: any) => o.value ?? o),
    [selectedOwnership]
  );
  const selectedBlockValues = useMemo(
    () => selectedBlock.map((b: any) => Number(b.value ?? b)),
    [selectedBlock]
  );
  const selectedGeneratorNamesValues = useMemo(
    () => selectedGeneratorName.map((g: any) => g.value ?? g),
    [selectedGeneratorName]
  );

  // Original row map (stable key)
  const originalRowMap = useMemo(() => {
    const m = new Map<string, any>();
    for (const r of data) m.set(`${r.generator}__${r.time_slot__slot}`, r);
    return m;
  }, [data]);

  const hasRowChanged = useCallback((orig: any, mod: any) => {
    if (!orig) return true;
    for (const f of comparableFields) {
      const ov = orig[f];
      const mv = mod[f];
      if (numericFieldSet.has(f)) {
        const on = ov === "" || ov == null ? null : Number(ov);
        const mn = mv === "" || mv == null ? null : Number(mv);
        if (on !== mn) return true;
      } else if (ov !== mv) return true;
    }
    return false;
  }, []);

  // Memo of changed rows
  const changedRows = useMemo(() => {
    if (!modifiedData.length) return [];
    const out: any[] = [];
    for (const mod of modifiedData) {
      const orig = originalRowMap.get(
        `${mod.generator}__${mod.time_slot__slot}`
      );
      if (hasRowChanged(orig, mod)) out.push(mod);
    }
    return out;
  }, [modifiedData, originalRowMap, hasRowChanged]);

  // Filtered generator name options (depends only on data + selected type/ownership)
  const filteredGeneratorNameOptions = useMemo(() => {
    if (!modifiedData.length) return [];
    const set = new Set<string>();
    for (const row of modifiedData) {
      if (
        (selectedTypeValues.length === 0 ||
          selectedTypeValues.includes(row.generator_type)) &&
        (selectedOwnershipValues.length === 0 ||
          selectedOwnershipValues.includes(row.ownership))
      ) {
        set.add(row.generator);
      }
    }
    return Array.from(set)
      .sort()
      .map((g) => ({ value: g, label: g }));
  }, [modifiedData, selectedTypeValues, selectedOwnershipValues]);

  // Prune generator selections if underlying options shrink
  useEffect(() => {
    if (!selectedGeneratorName.length) return;
    const allowed = new Set(
      filteredGeneratorNameOptions.map((o: any) => o.value)
    );
    const next = selectedGeneratorName.filter((g: any) =>
      allowed.has(g.value ?? g)
    );
    if (next.length !== selectedGeneratorName.length)
      setSelectedGeneratorName(next);
  }, [filteredGeneratorNameOptions, selectedGeneratorName]);

  const buildUpdatesObject = () => {
    const updates: Record<string, number> = {};
    if (declaredCapacity !== "")
      updates.total_capacity = Number(declaredCapacity);
    if (variableCharge !== "") updates.vc = Number(variableCharge);
    if (mustRun !== "") updates.must_run = Number(mustRun);
    if (buyMargin !== "") updates.margin_buy = Number(buyMargin);
    if (sellMargin !== "")
      updates.margin_conservative_sell = Number(sellMargin);
    if (sellMarginAggressive !== "")
      updates.margin_aggressive_sell = Number(sellMarginAggressive);
    return updates;
  };

  const handleApply = () => {
    const updates = buildUpdatesObject();
    if (!Object.keys(updates).length) return;

    const selectedGenNames = selectedGeneratorNamesValues;
    const blockNums = selectedBlockValues;

    setModifiedData((prev) => {
      if (!prev.length) return prev;
      let changed = false;
      const next = prev.map((row: any) => {
        const blockMatch =
          !blockNums.length || blockNums.includes(Number(row.time_slot__slot));
        const typeMatch =
          !selectedTypeValues.length ||
          selectedTypeValues.includes(row.generator_type);
        const ownershipMatch =
          !selectedOwnershipValues.length ||
          selectedOwnershipValues.includes(row.ownership);
        const generatorMatch =
          !selectedGenNames.length || selectedGenNames.includes(row.generator);
        if (blockMatch && typeMatch && ownershipMatch && generatorMatch) {
          changed = true;
          return { ...row, ...updates };
        }
        return row;
      });
      return changed ? next : prev;
    });

    resetForm();
  };

  const handleReset = () => {
    resetForm();
    setModifiedData(data);
  };

  const handleSave = () => {
    let pending = [...changedRows];

    // Include current form inputs not yet applied
    const pendingUpdates = buildUpdatesObject();
    if (Object.keys(pendingUpdates).length) {
      const targets = (modifiedData.length ? modifiedData : data).filter(
        (row: any) => {
          const typeOk =
            !selectedTypeValues.length ||
            selectedTypeValues.includes(row.generator_type);
          const ownershipOk =
            !selectedOwnershipValues.length ||
            selectedOwnershipValues.includes(row.ownership);
          const blockOk =
            !selectedBlockValues.length ||
            selectedBlockValues.includes(Number(row.time_slot__slot));
          const generatorOk =
            !selectedGeneratorNamesValues.length ||
            selectedGeneratorNamesValues.includes(row.generator);
          return typeOk && ownershipOk && blockOk && generatorOk;
        }
      );
      const map = new Map<string, any>();
      pending.forEach((r) =>
        map.set(`${r.generator}__${r.time_slot__slot}`, r)
      );
      for (const r of targets) {
        const k = `${r.generator}__${r.time_slot__slot}`;
        map.set(k, { ...r, ...pendingUpdates });
      }
      pending = Array.from(map.values());
    }

    if (!pending.length) {
      onClose();
      return;
    }

    onLocalApply?.(pending);
    onClose();
    resetForm();
  };

  return (
    <div className="z-[100000]">
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        width="774px"
        showCloseButton={false}
      >
        <div
          className="p-3"
          style={{
            backgroundColor: "#FFFFFF",
            borderBottom: "1px solid #DFEAF2",
          }}
        >
          <div className="flex items-center justify-between">
            <h2 className="text-base font-medium" style={{ color: "#525062" }}>
              Customize Generator Parameters
            </h2>
            <button
              onClick={() => {
                onClose();
                resetForm();
                setModifiedData(data);
              }}
              className="hover:bg-gray-200 p-1 rounded transition-colors"
              style={{ color: "#6C757D" }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="flex" style={{ minHeight: "400px" }}>
          <div className="flex bg-[#F3F5F8] p-3" style={{ minHeight: "400px" }}>
            <div
              className="p-4 space-y-3"
              style={{
                width: "280px",
                backgroundColor: "#FFFFFFA3",
                border: "1px solid #E1E5E9",
                borderRadius: "15px",
              }}
            >
              <div>
                <h2
                  className="text-base font-medium mb-4"
                  style={{ color: "#6C757D" }}
                >
                  Filters
                  <hr className="mt-1 border-[#DFEAF2]" />
                </h2>
                <label
                  className="block text-xs font-medium mb-1"
                  style={{ color: "#6F6D7C" }}
                >
                  Time Block
                </label>
                <SearchBox
                  options={uniqueBlocks.map((b: any) => ({
                    value: b,
                    label: b,
                  }))}
                  isMany
                  placeholder="Search Block"
                  selectedOptions={selectedBlock}
                  onChange={setSelectedBlock}
                />
              </div>
              <div>
                <label
                  className="block text-xs font-medium mb-1"
                  style={{ color: "#6F6D7C" }}
                >
                  Generator Type
                </label>
                <SearchBox
                  options={uniqueGeneratorTypes.map((g: any) => ({
                    value: g,
                    label: g,
                  }))}
                  isMany
                  placeholder="Search Generator Type"
                  selectedOptions={selectedGeneratorType}
                  onChange={setSelectedGeneratorType}
                />
              </div>
              <div>
                <label
                  className="block text-xs font-medium mb-1"
                  style={{ color: "#6F6D7C" }}
                >
                  Generator Ownership
                </label>
                <SearchBox
                  options={uniqueOwnership.map((o: any) => ({
                    value: o,
                    label: o,
                  }))}
                  isMany
                  placeholder="Search Ownership"
                  selectedOptions={selectedOwnership}
                  onChange={setSelectedOwnership}
                />
              </div>
              <div>
                <label
                  className="block text-xs font-medium mb-1"
                  style={{ color: "#6F6D7C" }}
                >
                  Generator Name
                </label>
                <SearchBox
                  menuMaxHeight={140}
                  options={filteredGeneratorNameOptions}
                  isMany
                  placeholder="Search Generator Name"
                  selectedOptions={selectedGeneratorName}
                  onChange={setSelectedGeneratorName}
                />
              </div>

              <div className="bg-white !m-0">
                {selectedGenerators.map((g) => (
                  <div
                    key={g}
                    className="flex items-center justify-between py-1 px-2 rounded-lg"
                    style={{ backgroundColor: "#FFF" }}
                  >
                    <span className="text-xs" style={{ color: "#6C757D" }}>
                      {g}
                    </span>
                    <button
                      onClick={() => removeGenerator(g)}
                      className="hover:bg-gray-300 p-1 rounded-b-lg transition-colors"
                      style={{ color: "#ADB5BD" }}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-4 bg-[#F3F5F8] p-3">
            <div
              className="p-4 space-y-3"
              style={{
                height: "100%",
                backgroundColor: "#FFFFFFA3",
                border: "1px solid #E1E5E9",
                borderRadius: "15px",
              }}
            >
              <div>
                <h2
                  className="text-base font-medium mb-4"
                  style={{ color: "#6C757D" }}
                >
                  Customize Fields
                  <hr className="mt-1 border-[#DFEAF2]" />
                </h2>
              </div>
              <div className="flex flex-col justify-between gap-10">
                <div>
                  <div className="mb-4">
                    <label
                      className="block text-xs font-medium mb-1"
                      style={{ color: "#6C757D" }}
                    >
                      Variable Charge (VC)
                    </label>
                    <input
                      step="any"
                      inputMode="decimal"
                      type="number"
                      value={variableCharge}
                      onChange={(e) => setVariableCharge(e.target.value)}
                      className="w-full px-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-1"
                      style={{
                        border: "1px solid #CED4DA",
                        color: "#495057",
                        backgroundColor: "#FFFFFF",
                        fontSize: "13px",
                      }}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label
                        className="block text-xs font-medium mb-1"
                        style={{ color: "#6C757D" }}
                      >
                        Must Run (MR)
                      </label>
                      <input
                        step="any"
                        inputMode="decimal"
                        type="number"
                        value={mustRun}
                        onChange={(e) => setMustRun(e.target.value)}
                        className="w-full px-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-1"
                        style={{
                          border: "1px solid #CED4DA",
                          color: "#495057",
                          backgroundColor: "#FFFFFF",
                          fontSize: "13px",
                        }}
                      />
                    </div>
                    <div>
                      <label
                        className="block text-xs font-medium mb-1"
                        style={{ color: "#6C757D" }}
                      >
                        Buy Margin (BM)
                      </label>
                      <input
                        step="any"
                        inputMode="decimal"
                        type="number"
                        value={buyMargin}
                        onChange={(e) => setBuyMargin(e.target.value)}
                        className="w-full px-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-1"
                        style={{
                          border: "1px solid #CED4DA",
                          color: "#495057",
                          backgroundColor: "#FFFFFF",
                          fontSize: "13px",
                        }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label
                        className="block text-xs font-medium mb-1"
                        style={{ color: "#6C757D" }}
                      >
                        Sell Margin (SM)
                      </label>
                      <input
                        step="any"
                        inputMode="decimal"
                        type="number"
                        value={sellMargin}
                        onChange={(e) => setSellMargin(e.target.value)}
                        className="w-full px-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-1"
                        style={{
                          border: "1px solid #CED4DA",
                          color: "#495057",
                          backgroundColor: "#FFFFFF",
                          fontSize: "13px",
                        }}
                      />
                    </div>
                    <div>
                      <label
                        className="block text-xs font-medium mb-1"
                        style={{ color: "#6C757D" }}
                      >
                        Sell Margin Aggressive (SAM)
                      </label>
                      <input
                        step="any"
                        inputMode="decimal"
                        type="number"
                        value={sellMarginAggressive}
                        onChange={(e) =>
                          setSellMarginAggressive(e.target.value)
                        }
                        className="w-full px-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-1"
                        style={{
                          border: "1px solid #CED4DA",
                          color: "#495057",
                          backgroundColor: "#FFFFFF",
                          fontSize: "13px",
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-center">
                  <button
                    onClick={handleApply}
                    className="flex items-center justify-center w-[130px] h-[39px] bg-[#FFFAF6] text-[#F1935C] border-[0.5px] border-[#F1935C] rounded-lg py-2.5 px-10 font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
                    disabled={
                      !(
                        declaredCapacity ||
                        variableCharge ||
                        mustRun ||
                        buyMargin ||
                        sellMargin ||
                        sellMarginAggressive
                      )
                    }
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="px-4 py-3 flex justify-end space-x-3"
          style={{ backgroundColor: "#FFFFFF", borderTop: "1px solid #E1E5E9" }}
        >
          <button
            type="button"
            onClick={handleReset}
            className="px-4 py-2 text-sm rounded border border-[#FF8C42] hover:bg-gray-50 font-medium transition-colors"
            style={{
              color: "#F1935C",
              backgroundColor: "#FFFAF6",
              fontSize: "14px",
            }}
          >
            Clear All
          </button>
          <button
            disabled={loading || changedRows.length === 0}
            onClick={handleSave}
            className="flex items-center gap-2 px-3 text-sm rounded disabled:text-[#A9A8B1] disabled:bg-[#E2E2E5] bg-[#F1935C] text-white font-medium"
          >
            {loading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ModFilter;
